import React, { useEffect, useState } from 'react';
import { apiClient } from '../../services/apiClient';

interface HealthData {
  celery_workers_active: number;
  last_aggregation_run: string;
  failed_aggregation_count: number;
  event_ingestion_errors_24h: number;
  cache_status: string;
  db_size: string | null;
}

const Health: React.FC = () => {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rerunLoading, setRerunLoading] = useState(false);

  const fetchHealth = async () => {
    try {
      const data = await apiClient.get('/platform/admin/health/');
      setHealth(data);
    } catch {
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 30000);
    return () => clearInterval(id);
  }, []);

  const handleRerun = async () => {
    if (!window.confirm('Re-run the aggregation task? This will process daily metrics.')) return;
    setRerunLoading(true);
    try {
      await apiClient.post('/platform/admin/health/rerun-aggregation/', {});
      await fetchHealth();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to enqueue task');
    } finally {
      setRerunLoading(false);
    }
  };

  if (loading) return <div className="text-fg-muted">Loading...</div>;
  if (!health) return <div className="text-fg-muted">Failed to load system health</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">System Health</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-app border border-border rounded-lg p-4">
          <p className="text-sm text-fg-muted">Celery Workers Active</p>
          <p className="text-2xl font-semibold text-white mt-1">{health.celery_workers_active}</p>
        </div>
        <div className="bg-app border border-border rounded-lg p-4">
          <p className="text-sm text-fg-muted">Last Aggregation Run</p>
          <p className="text-lg font-semibold text-white mt-1">{health.last_aggregation_run}</p>
        </div>
        <div className="bg-app border border-border rounded-lg p-4">
          <p className="text-sm text-fg-muted">Failed Aggregation Count</p>
          <p className={`text-2xl font-semibold mt-1 ${health.failed_aggregation_count > 0 ? 'text-red-400' : 'text-white'}`}>
            {health.failed_aggregation_count}
          </p>
        </div>
        <div className="bg-app border border-border rounded-lg p-4">
          <p className="text-sm text-fg-muted">Event Ingestion Errors (24h)</p>
          <p className={`text-2xl font-semibold mt-1 ${health.event_ingestion_errors_24h > 0 ? 'text-amber-400' : 'text-white'}`}>
            {health.event_ingestion_errors_24h}
          </p>
        </div>
        <div className="bg-app border border-border rounded-lg p-4">
          <p className="text-sm text-fg-muted">Cache Status</p>
          <p className={`text-2xl font-semibold mt-1 ${health.cache_status === 'ok' ? 'text-success-fg' : 'text-red-400'}`}>
            {health.cache_status}
          </p>
        </div>
        {health.db_size && (
          <div className="bg-app border border-border rounded-lg p-4">
            <p className="text-sm text-fg-muted">Database Size</p>
            <p className="text-2xl font-semibold text-white mt-1">{health.db_size}</p>
          </div>
        )}
      </div>
      <div className="bg-app border border-border rounded-lg p-4">
        <h2 className="text-lg font-semibold text-white mb-2">Actions</h2>
        <button
          onClick={handleRerun}
          disabled={rerunLoading}
          className="px-4 py-2 rounded-lg bg-amber-500 text-fg font-medium hover:bg-amber-600 disabled:opacity-50"
        >
          {rerunLoading ? 'Enqueuing...' : 'Re-run Aggregation Task'}
        </button>
        <p className="text-sm text-fg-muted mt-2">
          Enqueues the Celery aggregate_daily_metrics task. Safe to run manually.
        </p>
      </div>
    </div>
  );
};

export default Health;
